/**
 * Device Orientation AR Service
 * Fallback AR implementation using device sensors and camera
 */

import { EventEmitter } from '@/components/SplatViewer/event-emitter'

interface DeviceOrientationAROptions {
  onSessionStart?: () => void
  onSessionEnd?: () => void
  onError?: (error: Error) => void
}

export class DeviceOrientationAR extends EventEmitter {
  private videoElement: HTMLVideoElement | null = null
  private stream: MediaStream | null = null
  private isActive = false
  private options: DeviceOrientationAROptions
  
  // Device orientation data
  private alpha = 0 // Z axis rotation (compass)
  private beta = 0  // X axis rotation (pitch)
  private gamma = 0 // Y axis rotation (roll)
  
  // Device motion data
  private accelerationX = 0
  private accelerationY = 0
  private accelerationZ = 0
  private rotationRateAlpha = 0
  private rotationRateBeta = 0
  private rotationRateGamma = 0
  
  // Initial orientation for calibration
  private initialAlpha: number | null = null
  private initialBeta: number | null = null
  private initialGamma: number | null = null
  
  constructor(options: DeviceOrientationAROptions = {}) {
    super()
    this.options = options
  }
  
  /**
   * Check if Device Orientation AR is supported
   */
  static async isSupported(): Promise<boolean> {
    const hasOrientation = typeof DeviceOrientationEvent !== 'undefined'
    const hasCamera = navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    
    if (!hasOrientation || !hasCamera) {
      return false
    }
    
    // Check if we need permission for device orientation (iOS 13+)
    if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
      try {
        const response = await (DeviceOrientationEvent as any).requestPermission()
        return response === 'granted'
      } catch (error) {
        return false
      }
    }
    
    return true
  }
  
  /**
   * Start AR session
   */
  async startSession(_canvas: HTMLCanvasElement): Promise<void> {
    if (this.isActive) {
      throw new Error('AR session already active')
    }
    
    try {
      // Request camera permission and start video stream
      await this.startCamera()
      
      // Request device orientation permission on iOS
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        const response = await (DeviceOrientationEvent as any).requestPermission()
        if (response !== 'granted') {
          throw new Error('Device orientation permission denied')
        }
      }
      
      // Start listening to device orientation and motion
      this.startOrientationTracking()
      this.startMotionTracking()
      
      this.isActive = true
      this.emit('sessionStart')
      this.options.onSessionStart?.()
      
    } catch (error) {
      this.cleanup()
      const err = error instanceof Error ? error : new Error('Failed to start AR session')
      this.emit('error', err)
      this.options.onError?.(err)
      throw err
    }
  }
  
  /**
   * End AR session
   */
  async endSession(): Promise<void> {
    if (!this.isActive) return
    
    this.cleanup()
    this.isActive = false
    this.emit('sessionEnd')
    this.options.onSessionEnd?.()
  }
  
  /**
   * Get current device orientation
   */
  getOrientation(): { alpha: number; beta: number; gamma: number; absolute?: boolean } {
    // Return calibrated orientation
    const calibratedAlpha = this.initialAlpha !== null ? this.alpha - this.initialAlpha : this.alpha
    const calibratedBeta = this.initialBeta !== null ? this.beta - this.initialBeta : this.beta
    const calibratedGamma = this.initialGamma !== null ? this.gamma - this.initialGamma : this.gamma
    
    return {
      alpha: calibratedAlpha,
      beta: calibratedBeta,
      gamma: calibratedGamma,
      absolute: (window.DeviceOrientationEvent as any).absolute || false
    }
  }
  
  /**
   * Calibrate orientation (set current orientation as zero)
   */
  calibrate(): void {
    this.initialAlpha = this.alpha
    this.initialBeta = this.beta
    this.initialGamma = this.gamma
  }
  
  /**
   * Get video element for rendering as background
   */
  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement
  }
  
  /**
   * Start camera stream
   */
  private async startCamera(): Promise<void> {
    // Create video element
    this.videoElement = document.createElement('video')
    this.videoElement.autoplay = true
    this.videoElement.playsInline = true
    this.videoElement.muted = true
    
    // Request camera access
    const constraints = {
      video: {
        facingMode: 'environment', // Rear camera
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      },
      audio: false
    }
    
    this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    this.videoElement.srcObject = this.stream
    
    // Wait for video to be ready
    await new Promise((resolve) => {
      this.videoElement!.onloadedmetadata = resolve
    })
  }
  
  /**
   * Start device orientation tracking
   */
  private startOrientationTracking(): void {
    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (event.alpha !== null) this.alpha = event.alpha
      if (event.beta !== null) this.beta = event.beta
      if (event.gamma !== null) this.gamma = event.gamma
      
      // Calibrate on first reading
      if (this.initialAlpha === null && event.alpha !== null) {
        this.calibrate()
      }
      
      this.emit('orientationChange', this.getOrientation())
    }
    
    window.addEventListener('deviceorientation', handleOrientation)
    
    // Store handler for cleanup
    ;(this as any).orientationHandler = handleOrientation
  }
  
  /**
   * Start device motion tracking
   */
  private startMotionTracking(): void {
    const handleMotion = (event: DeviceMotionEvent) => {
      // Update acceleration data
      if (event.acceleration) {
        this.accelerationX = event.acceleration.x || 0
        this.accelerationY = event.acceleration.y || 0
        this.accelerationZ = event.acceleration.z || 0
      }
      
      // Update rotation rate data
      if (event.rotationRate) {
        this.rotationRateAlpha = event.rotationRate.alpha || 0
        this.rotationRateBeta = event.rotationRate.beta || 0
        this.rotationRateGamma = event.rotationRate.gamma || 0
      }
      
      this.emit('motionChange', {
        acceleration: {
          x: this.accelerationX,
          y: this.accelerationY,
          z: this.accelerationZ
        },
        rotationRate: {
          alpha: this.rotationRateAlpha,
          beta: this.rotationRateBeta,
          gamma: this.rotationRateGamma
        }
      })
    }
    
    window.addEventListener('devicemotion', handleMotion)
    
    // Store handler for cleanup
    ;(this as any).motionHandler = handleMotion
  }
  
  /**
   * Get current motion data
   */
  getMotionData() {
    return {
      acceleration: {
        x: this.accelerationX,
        y: this.accelerationY,
        z: this.accelerationZ
      },
      rotationRate: {
        alpha: this.rotationRateAlpha,
        beta: this.rotationRateBeta,
        gamma: this.rotationRateGamma
      }
    }
  }
  
  /**
   * Clean up resources
   */
  private cleanup(): void {
    // Stop camera
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    
    if (this.videoElement) {
      this.videoElement.srcObject = null
      this.videoElement = null
    }
    
    // Stop orientation tracking
    if ((this as any).orientationHandler) {
      window.removeEventListener('deviceorientation', (this as any).orientationHandler)
      ;(this as any).orientationHandler = null
    }
    
    // Stop motion tracking
    if ((this as any).motionHandler) {
      window.removeEventListener('devicemotion', (this as any).motionHandler)
      ;(this as any).motionHandler = null
    }
    
    // Reset calibration
    this.initialAlpha = null
    this.initialBeta = null
    this.initialGamma = null
  }
}